import React from 'react';
import { Link } from 'react-router-dom';
import { SkewButton, SectionTitle, Card, GradientDivider } from '../components/UIComponents';
import { Salad, Coffee, Apple, Leaf, Heart, Zap, Check, ShoppingBag, Sparkles } from 'lucide-react';

const GOALTRITION_LOGO = "https://customer-assets.emergentagent.com/job_goalgeous-hub/artifacts/s78jupvz_3985D091-2621-4699-AF7F-50FB5CC34B4B.png";
const MAIN_LOGO = "https://customer-assets.emergentagent.com/job_goalgeous-hub/artifacts/ad5dhro0_Untitled%20design.png";

const proteinShakes = [
  {
    name: "THE GOAL GETTER",
    description: "Vanilla protein shake",
    base: "Water or almond milk base",
    price: "$6",
    highlight: false
  },
  {
    name: "PINK POWER",
    description: "Vanilla protein + strawberries + banana",
    base: "Water or almond milk base",
    price: "$8",
    highlight: true
  },
  {
    name: "BERRY GOAL BURST",
    description: "Vanilla protein + Mary Berry mix",
    base: "Water or almond milk base",
    price: "$8",
    highlight: true
  }
];

const mealPlans = [
  {
    name: "Clean & Lean",
    description: "A balanced meal plan focused on lean proteins, complex carbs, and plenty of vegetables.",
    duration: "4 weeks",
    price: "$79",
    features: ["28 days of meals", "Shopping lists", "Prep guides", "Macro tracking"]
  },
  {
    name: "Power Fuel",
    description: "High-protein meal plan designed for muscle building and recovery.",
    duration: "4 weeks",
    price: "$89",
    features: ["28 days of meals", "High protein focus", "Pre/post workout meals", "Supplement guide"]
  },
  {
    name: "Detox Reset",
    description: "A cleansing meal plan to reset your system and kickstart your metabolism.",
    duration: "2 weeks",
    price: "$59",
    features: ["14 days of meals", "Smoothie recipes", "Detox tips", "Energy boosting foods"]
  }
];

const principles = [
  { icon: Leaf, title: "Clean Eating", description: "Whole foods, minimal processing" },
  { icon: Apple, title: "Balanced Nutrition", description: "Proper macros for your goals" },
  { icon: Heart, title: "Sustainable Habits", description: "Lifestyle, not a diet" },
  { icon: Zap, title: "Performance Fuel", description: "Food that powers your workouts" },
];

export const GoalTritonPage = () => {
  return (
    <div data-testid="goaltrition-page" className="bg-brand-black pt-20">
      {/* Hero Section with GOAL-Trition Logo */}
      <section data-testid="goaltrition-hero" className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-black via-brand-dark to-brand-black" />
        
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          {/* GOAL-Trition Logo */}
          <img 
            src={GOALTRITION_LOGO} 
            alt="GOAL-Trition - Fuel Your Strength" 
            className="h-64 md:h-80 mx-auto mb-8"
          />
          
          <p className="font-montserrat text-lg text-brand-gray leading-relaxed max-w-2xl mx-auto mb-8">
            Nutrition is the foundation of transformation. GOAL-Trition provides you with clean eating meal plans, protein shakes, and the knowledge to fuel your body for success.
          </p>
          
          <div className="flex flex-wrap gap-4 justify-center">
            <SkewButton href="#shake-menu" testId="goaltrition-shake-cta">
              View Shake Menu
            </SkewButton>
            <SkewButton variant="secondary" href="#meal-plans" testId="goaltrition-meals-cta">
              Explore Meal Plans
            </SkewButton>
          </div>
        </div>
      </section>

      {/* Protein Shake Menu Section */}
      <section id="shake-menu" data-testid="shake-menu-section" className="py-20 bg-gradient-to-b from-brand-dark to-brand-black relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-10 left-10 w-32 h-32 bg-brand-pink/10 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-brand-lime/10 rounded-full blur-3xl" />
        
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          {/* Menu Header */}
          <div className="text-center mb-12">
            <div className="inline-block bg-brand-pink/20 px-6 py-2 rounded-full mb-6">
              <span className="font-oswald text-sm uppercase tracking-[0.3em] text-brand-pink">
                Protein Shake Menu
              </span>
            </div>
            <h2 className="font-oswald text-4xl md:text-6xl font-bold uppercase tracking-tighter text-white mb-4">
              Fuel Your <span className="text-brand-lime">Strength</span>
            </h2>
            <p className="font-montserrat text-brand-gray max-w-xl mx-auto">
              Fresh, made-to-order protein shakes to power your workouts and recovery.
            </p>
          </div>
          
          {/* Shake Menu Cards */}
          <div className="space-y-6">
            {proteinShakes.map((shake, index) => (
              <div 
                key={shake.name}
                data-testid={`shake-${index}`}
                className={`bg-brand-dark border-2 ${shake.highlight ? 'border-brand-pink/50' : 'border-white/10'} p-6 md:p-8 relative overflow-hidden group hover:border-brand-pink transition-all duration-300`}
              >
                {/* Accent line */}
                <div className={`absolute left-0 top-0 bottom-0 w-1 ${shake.highlight ? 'bg-brand-pink' : 'bg-brand-lime'}`} />
                
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <h3 className={`font-oswald text-2xl md:text-3xl uppercase tracking-tight mb-2 ${shake.highlight ? 'text-brand-pink' : 'text-brand-lime'}`}>
                      {shake.name}
                    </h3>
                    <p className="font-montserrat text-white mb-1">
                      {shake.description}
                    </p>
                    <p className="font-montserrat text-sm text-brand-gray">
                      {shake.base}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`font-oswald text-4xl md:text-5xl ${shake.highlight ? 'text-brand-pink' : 'text-brand-lime'}`}>
                      {shake.price}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pre-order CTA */}
          <div className="mt-12 text-center">
            <div className="bg-gradient-to-r from-brand-pink/20 via-brand-dark to-brand-lime/20 border border-white/10 p-8">
              <p className="font-oswald text-xl uppercase tracking-wider text-white mb-4">
                Pre-Order Your Shake
              </p>
              <p className="font-montserrat text-brand-gray mb-6">
                Skip the wait! Pre-order and have your shake ready when you arrive.
              </p>
              <SkewButton href="https://viagro.com" testId="preorder-shake-btn">
                Pre-Order Now
              </SkewButton>
            </div>
          </div>

          {/* Tagline */}
          <div className="mt-12 text-center">
            <p className="font-oswald text-xl md:text-2xl uppercase tracking-wider text-white">
              Train with purpose. Fuel with <span className="text-brand-lime">GOAL</span>-<span className="text-brand-pink">Trition</span>. 
              <span className="ml-2">💚🩷</span>
            </p>
          </div>
        </div>
      </section>

      {/* Principles Section */}
      <section data-testid="principles-section" className="py-20 bg-brand-dark">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <SectionTitle 
            title="Our Philosophy" 
            subtitle="How We Fuel"
            align="center"
            className="mb-16"
          />
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {principles.map((principle, index) => (
              <div 
                key={principle.title}
                data-testid={`principle-${principle.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="text-center group"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-brand-lime/10 flex items-center justify-center group-hover:bg-brand-lime/20 transition-colors">
                  <principle.icon size={28} className="text-brand-lime" />
                </div>
                <h3 className="font-oswald text-lg uppercase tracking-wide text-white mb-2">{principle.title}</h3>
                <p className="font-montserrat text-sm text-brand-gray">{principle.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Meal Plans Section */}
      <section id="meal-plans" data-testid="mealplans-section" className="py-20 bg-brand-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <SectionTitle 
            title="Meal Plans" 
            subtitle="Eat With Purpose"
            className="mb-16"
          />
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {mealPlans.map((plan, index) => (
              <Card 
                key={plan.name}
                testId={`mealplan-${index}`}
                className="p-8 flex flex-col"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-lg bg-brand-lime/10 flex items-center justify-center">
                    <Salad size={24} className="text-brand-lime" />
                  </div>
                  <div>
                    <h3 className="font-oswald text-xl uppercase text-white">{plan.name}</h3>
                    <span className="text-sm text-brand-gray">{plan.duration}</span>
                  </div>
                </div>
                <p className="font-montserrat text-brand-gray leading-relaxed mb-6 flex-grow">
                  {plan.description}
                </p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-sm text-brand-gray">
                      <Check size={14} className="text-brand-lime" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex items-center justify-between">
                  <span className="font-oswald text-3xl text-brand-lime">{plan.price}</span>
                  <SkewButton 
                    href="https://viagro.com"
                    testId={`buy-mealplan-${index}`}
                  >
                    Get Plan
                  </SkewButton>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section data-testid="how-it-works" className="py-20 bg-brand-dark">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <SectionTitle 
            title="How It Works" 
            subtitle="Simple Process"
            align="center"
            accent="pink"
            className="mb-16"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Choose Your Plan", description: "Select a meal plan that fits your goals and lifestyle." },
              { step: "02", title: "Get Your Guide", description: "Receive your complete meal plan with recipes and shopping lists." },
              { step: "03", title: "Fuel Your Goals", description: "Follow the plan, prep your meals, and watch the transformation." }
            ].map((item, index) => (
              <div key={item.step} data-testid={`step-${index}`} className="text-center">
                <span className="font-oswald text-6xl text-brand-pink/20">{item.step}</span>
                <h3 className="font-oswald text-xl uppercase text-white mb-3 -mt-4">{item.title}</h3>
                <p className="font-montserrat text-brand-gray">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="goaltrition-cta" className="py-24 bg-brand-black relative overflow-hidden">
        {/* Background glow effects */}
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-brand-lime/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-brand-pink/10 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <img 
            src={GOALTRITION_LOGO} 
            alt="GOAL-TRITION" 
            className="h-48 mx-auto mb-8"
          />
          <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase text-white mb-6">
            Fuel Your <span className="text-brand-lime">Transformation</span>
          </h2>
          <p className="font-montserrat text-lg text-brand-gray mb-4">
            Clean eating made simple. Start your nutrition journey today.
          </p>
          <p className="font-oswald text-xl uppercase tracking-wider text-white mb-8">
            Train with purpose. Fuel with <span className="text-brand-lime">GOAL</span>-<span className="text-brand-pink">Trition</span>. 💚🩷
          </p>
          <SkewButton href="https://viagro.com" testId="goaltrition-final-cta">
            Start Eating Clean
          </SkewButton>
        </div>
      </section>
    </div>
  );
};
